/**
 * SessionPlanView
 *
 * Read-only expanded view of the active session plan.
 * State arrives via BroadcastChannel from the main window.
 *
 * Sections:
 * 1. Header — title + scene progress
 * 2. The Truth — always-visible gold box
 * 3. Scenes timeline — vertical list with status indicators
 * 4. Clue Cards — discovered/undiscovered checklist
 * 5. Session NPCs — cards with tell, pressure line, alternates
 */

import { useSessionPlanStore } from '../../stores/sessionPlanStore';
import { Icon } from '../Icon';
import type { SessionScene, ClueCard, SessionNPC, ChaseTrack } from '@machina/core';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';
const SILVER = '#c0c0c0';
const GREEN = '#4caf50';
const RED = '#dc143c';

/** Scale a base font size by the offset (0 = small/current, 2 = medium, 4 = large) */
function fs(base: number, scale: number) { return base + scale; }

interface ViewProps { fontScale: number; }

export function SessionPlanView({ fontScale }: ViewProps) {
  const session = useSessionPlanStore((s) => s.getActiveSession());
  const f = (base: number) => fs(base, fontScale);

  if (!session) {
    return (
      <div style={styles.empty}>
        <Icon name="scroll" size={28} color={GOLD_DIM} />
        <div style={{ color: GOLD_DIM, fontSize: f(13), fontStyle: 'italic' }}>
          No active session
        </div>
        <div style={{ color: 'rgba(192, 192, 192, 0.3)', fontSize: f(11), maxWidth: 240, textAlign: 'center' }}>
          Load a session in the GM Tray to see the plan here.
        </div>
      </div>
    );
  }

  const completedScenes = session.scenes.filter((s) => s.status === 'completed').length;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ ...styles.title, fontSize: f(18) }}>{session.name}</div>
        {session.subtitle && (
          <div style={{ ...styles.subtitle, fontSize: f(11) }}>{session.subtitle}</div>
        )}
        <div style={{ ...styles.progress, fontSize: f(10) }}>
          <Icon name="compass" size={f(11)} color={SILVER} />
          Scene {Math.min(session.activeSceneIndex + 1, session.scenes.length)} of {session.scenes.length}
          {completedScenes > 0 && ` \u2014 ${completedScenes} completed`}
        </div>
      </div>

      {/* The Truth */}
      <div style={styles.truthBox}>
        <div style={{ ...styles.truthLabel, fontSize: f(9) }}>
          <Icon name="eye" size={f(11)} color={GOLD} />
          THE TRUTH
        </div>
        <div style={{ ...styles.truthText, fontSize: f(11) }}>{session.truth}</div>
      </div>

      {/* Core Loop */}
      {session.coreLoop && (
        <div style={styles.coreLoop}>
          <span style={{ color: GOLD_DIM, fontSize: f(10), letterSpacing: 1 }}>FLOW</span>
          <span style={{ color: SILVER, fontSize: f(11) }}>{session.coreLoop}</span>
        </div>
      )}

      {/* Scenes Timeline */}
      <SectionHeader label="SCENES" icon="flag" fontScale={fontScale} />
      <div style={styles.sceneList}>
        {session.scenes.map((scene, idx) => (
          <SceneCard key={scene.id} scene={scene} index={idx} clueCards={session.clueCards} fontScale={fontScale} />
        ))}
      </div>

      {/* Clue Cards */}
      <SectionHeader label="CLUE CARDS" icon="eye" fontScale={fontScale} />
      <div style={styles.clueList}>
        {session.clueCards.map((clue) => (
          <ClueCardRow key={clue.id} clue={clue} fontScale={fontScale} />
        ))}
      </div>

      {/* NPCs */}
      {session.npcs.length > 0 && (
        <>
          <SectionHeader label="SESSION NPCs" icon="users" fontScale={fontScale} />
          <div style={styles.npcList}>
            {session.npcs.map((npc) => (
              <NPCCard key={npc.id} npc={npc} fontScale={fontScale} />
            ))}
          </div>
        </>
      )}

      {/* Bottom padding */}
      <div style={{ height: 40 }} />
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function SectionHeader({ label, icon, fontScale }: { label: string; icon: string; fontScale: number }) {
  const f = (base: number) => fs(base, fontScale);
  return (
    <div style={{ ...styles.sectionHeader, fontSize: f(9) }}>
      <Icon name={icon as 'flag'} size={f(11)} color={GOLD} />
      <span>{label}</span>
      <div style={styles.sectionLine} />
    </div>
  );
}

function SceneCard({ scene, index, clueCards, fontScale }: { scene: SessionScene; index: number; clueCards: ClueCard[]; fontScale: number }) {
  const f = (base: number) => fs(base, fontScale);
  const isActive = scene.status === 'active';
  const isCompleted = scene.status === 'completed';
  const linkedClues = clueCards.filter((c) => scene.clueCardIds.includes(c.id));

  return (
    <div
      style={{
        ...styles.sceneCard,
        borderColor: isActive ? GOLD : isCompleted ? GREEN : 'rgba(192, 192, 192, 0.15)',
        backgroundColor: isActive ? 'rgba(212, 175, 55, 0.06)' : 'transparent',
      }}
    >
      {/* Scene header */}
      <div style={styles.sceneHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isCompleted ? (
            <Icon name="check" size={f(12)} color={GREEN} />
          ) : isActive ? (
            <Icon name="target" size={f(12)} color={GOLD} />
          ) : (
            <span style={{ color: 'rgba(192, 192, 192, 0.3)', fontSize: f(11), width: f(12), textAlign: 'center' }}>
              {index + 1}
            </span>
          )}
          <span style={{
            color: isActive ? GOLD : isCompleted ? GREEN : SILVER,
            fontSize: f(12),
            fontWeight: isActive ? 'bold' : 'normal',
          }}>
            {scene.name}
          </span>
        </div>
        <span style={{ color: 'rgba(192, 192, 192, 0.3)', fontSize: f(10) }}>
          {scene.location}
        </span>
      </div>

      {/* Scene body — always visible in assistant (it's a reference panel) */}
      {(scene.description || scene.skillChecks.length > 0 || scene.chase || scene.encounter || scene.notes) && (
        <div style={{ ...styles.sceneBody, opacity: isActive || isCompleted ? 1 : 0.7 }}>
          <div style={{ color: 'rgba(224, 220, 208, 0.8)', fontSize: f(11), lineHeight: 1.5 }}>
            {scene.description}
          </div>

          {scene.goal && (
            <div style={{ color: GOLD_DIM, fontSize: f(10), marginTop: 6, fontStyle: 'italic' }}>
              Goal: {scene.goal}
            </div>
          )}

          {/* Skill checks */}
          {scene.skillChecks.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 4 }}>CHECKS</div>
              {scene.skillChecks.map((check) => (
                <div key={check.id} style={styles.checkRow}>
                  <span style={{
                    color: check.resolved
                      ? (check.passed ? GREEN : RED)
                      : 'rgba(192, 192, 192, 0.5)',
                    fontSize: f(11),
                    minWidth: 14,
                  }}>
                    {check.resolved ? (check.passed ? '\u2713' : '\u2717') : '\u25CB'}
                  </span>
                  <span style={{ color: SILVER, fontSize: f(10), minWidth: 80 }}>{check.ability}</span>
                  <span style={{ color: GOLD_DIM, fontSize: f(10) }}>DC {check.dc}</span>
                  <span style={{ color: 'rgba(192, 192, 192, 0.4)', fontSize: f(10), flex: 1 }}>
                    {check.description}
                  </span>
                  {check.resolved && (
                    <span style={{ color: check.passed ? GREEN : RED, fontSize: f(10), fontStyle: 'italic' }}>
                      {check.passed ? check.onSuccess : (check.onFail || 'Failed')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Chase track */}
          {scene.chase && <ChaseTrackView chase={scene.chase} fontScale={fontScale} />}

          {/* Encounter */}
          {scene.encounter && (
            <div style={styles.encounterBox}>
              <div style={{ color: RED, fontSize: f(9), letterSpacing: 1, marginBottom: 4 }}>ENCOUNTER</div>
              <div style={{ color: SILVER, fontSize: f(11) }}>{scene.encounter.enemies}</div>
              {scene.encounter.notes && (
                <div style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), marginTop: 4, whiteSpace: 'pre-wrap' }}>
                  {scene.encounter.notes}
                </div>
              )}
            </div>
          )}

          {/* Linked clues */}
          {linkedClues.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 4 }}>CLUES IN THIS SCENE</div>
              {linkedClues.map((clue) => (
                <div key={clue.id} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                  <Icon name={clue.discovered ? 'eye' : 'eye-off'} size={f(10)} color={clue.discovered ? GREEN : 'rgba(192, 192, 192, 0.3)'} />
                  <span style={{ color: clue.discovered ? SILVER : 'rgba(192, 192, 192, 0.4)', fontSize: f(10) }}>
                    {clue.name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* GM notes */}
          {scene.notes && (
            <div style={styles.gmNotes}>
              <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 4 }}>GM NOTES</div>
              <div style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {scene.notes}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChaseTrackView({ chase, fontScale }: { chase: ChaseTrack; fontScale: number }) {
  const f = (base: number) => fs(base, fontScale);
  const progressPct = (chase.currentProgress / chase.progressNeeded) * 100;
  const failPct = (chase.currentFailures / chase.failuresAllowed) * 100;

  return (
    <div style={styles.chaseBox}>
      <div style={{ color: GOLD, fontSize: f(9), letterSpacing: 1, marginBottom: 6 }}>CHASE: {chase.name.toUpperCase()}</div>

      {/* Progress bar */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: f(10), marginBottom: 2 }}>
          <span style={{ color: GREEN }}>Progress {chase.currentProgress}/{chase.progressNeeded}</span>
          <span style={{ color: RED }}>Failures {chase.currentFailures}/{chase.failuresAllowed}</span>
        </div>
        <div style={styles.barTrack}>
          <div style={{ ...styles.barFill, width: `${progressPct}%`, backgroundColor: GREEN }} />
        </div>
        <div style={{ ...styles.barTrack, marginTop: 2 }}>
          <div style={{ ...styles.barFill, width: `${failPct}%`, backgroundColor: RED }} />
        </div>
      </div>

      {/* Options */}
      <div style={{ marginTop: 6 }}>
        <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 3 }}>OPTIONS</div>
        {chase.options.map((opt, i) => (
          <div key={i} style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), padding: '1px 0' }}>
            <span style={{ color: SILVER }}>{opt.ability}</span> \u2014 {opt.description}
            {opt.progressOnSuccess && opt.progressOnSuccess > 1 && (
              <span style={{ color: GREEN }}> (+{opt.progressOnSuccess})</span>
            )}
          </div>
        ))}
      </div>

      {/* Complications */}
      {chase.complications.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 3 }}>COMPLICATIONS (d{chase.complications.length})</div>
          {chase.complications.map((comp) => (
            <div key={comp.roll} style={{ color: 'rgba(192, 192, 192, 0.4)', fontSize: f(10), padding: '1px 0' }}>
              <span style={{ color: GOLD_DIM }}>{comp.roll}.</span> {comp.description}
              {comp.check && <span style={{ color: SILVER }}> ({comp.check.ability} DC {comp.check.dc})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Special rules */}
      {chase.specialRules && chase.specialRules.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 3 }}>SPECIAL RULES</div>
          {chase.specialRules.map((rule, i) => (
            <div key={i} style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), padding: '1px 0' }}>
              {rule}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClueCardRow({ clue, fontScale }: { clue: ClueCard; fontScale: number }) {
  const f = (base: number) => fs(base, fontScale);
  return (
    <div style={{
      ...styles.clueCard,
      borderColor: clue.discovered ? GREEN : 'rgba(192, 192, 192, 0.1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon
          name={clue.discovered ? 'eye' : 'eye-off'}
          size={f(12)}
          color={clue.discovered ? GREEN : 'rgba(192, 192, 192, 0.3)'}
        />
        <span style={{
          color: clue.discovered ? SILVER : 'rgba(192, 192, 192, 0.5)',
          fontSize: f(11),
          fontWeight: clue.discovered ? 'bold' : 'normal',
        }}>
          {clue.name}
        </span>
        {clue.check && (
          <span style={{ color: GOLD_DIM, fontSize: f(9) }}>
            {clue.check.ability} DC {clue.check.dc}
          </span>
        )}
      </div>
      <div style={{ color: 'rgba(224, 220, 208, 0.6)', fontSize: f(10), marginTop: 4, paddingLeft: 20 }}>
        {clue.front}
      </div>
      {clue.discovered && clue.back && (
        <div style={{ color: GOLD_DIM, fontSize: f(10), marginTop: 3, paddingLeft: 20, fontStyle: 'italic' }}>
          {clue.back}
        </div>
      )}
      {clue.leadsTo && (
        <div style={{ color: 'rgba(192, 192, 192, 0.3)', fontSize: f(9), marginTop: 2, paddingLeft: 20 }}>
          Leads to: {clue.leadsTo}
        </div>
      )}
    </div>
  );
}

function NPCCard({ npc, fontScale }: { npc: SessionNPC; fontScale: number }) {
  const f = (base: number) => fs(base, fontScale);
  return (
    <div style={styles.npcCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ color: GOLD, fontSize: f(12), fontWeight: 'bold' }}>
          {npc.alias || npc.name}
        </span>
        {npc.alias && (
          <span style={{ color: 'rgba(192, 192, 192, 0.4)', fontSize: f(10) }}>{npc.name}</span>
        )}
      </div>
      <div style={{ color: SILVER, fontSize: f(10), marginTop: 2, fontStyle: 'italic' }}>{npc.role}</div>
      <div style={{ color: 'rgba(224, 220, 208, 0.6)', fontSize: f(10), marginTop: 6, lineHeight: 1.5 }}>
        {npc.description}
      </div>
      {npc.style && (
        <div style={{ color: 'rgba(192, 192, 192, 0.4)', fontSize: f(10), marginTop: 4 }}>
          Style: {npc.style}
        </div>
      )}
      {npc.tell && (
        <div style={{ marginTop: 6 }}>
          <span style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1 }}>TELL: </span>
          <span style={{ color: SILVER, fontSize: f(10) }}>{npc.tell}</span>
        </div>
      )}
      {npc.pressureLine && (
        <div style={{ marginTop: 4, padding: '6px 10px', backgroundColor: 'rgba(212, 175, 55, 0.06)', borderLeft: `2px solid ${GOLD_DIM}` }}>
          <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 2 }}>PRESSURE LINE</div>
          <div style={{ color: GOLD, fontSize: f(11), fontStyle: 'italic' }}>"{npc.pressureLine}"</div>
        </div>
      )}
      {npc.alternates && npc.alternates.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 3 }}>ALTERNATES</div>
          {npc.alternates.map((alt, i) => (
            <div key={i} style={{ color: 'rgba(192, 192, 192, 0.4)', fontSize: f(10), padding: '2px 0' }}>
              {i + 1}. {alt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0 16px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    height: '100%',
    minHeight: 300,
  },
  header: {
    padding: '16px 0 12px',
    borderBottom: `1px solid ${GOLD_DIM}`,
  },
  title: {
    color: GOLD,
    fontWeight: 'bold',
    fontFamily: 'Georgia, serif',
  },
  subtitle: {
    color: 'rgba(192, 192, 192, 0.5)',
    fontStyle: 'italic',
    marginTop: 4,
  },
  progress: {
    color: SILVER,
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  truthBox: {
    margin: '12px 0',
    padding: '12px 14px',
    backgroundColor: 'rgba(212, 175, 55, 0.06)',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 2,
  },
  truthLabel: {
    color: GOLD,
    letterSpacing: 1.5,
    fontWeight: 'bold',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  truthText: {
    color: '#e0dcd0',
    lineHeight: 1.6,
  },
  coreLoop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0 10px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 0 6px',
    color: GOLD,
    letterSpacing: 1.5,
    fontWeight: 'bold',
  },
  sectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: GOLD_DIM,
  },
  sceneList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  sceneCard: {
    border: '1px solid rgba(192, 192, 192, 0.15)',
    borderRadius: 2,
    padding: '8px 12px',
    transition: 'border-color 0.3s',
  },
  sceneHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sceneBody: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid rgba(192, 192, 192, 0.08)',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '3px 0',
  },
  chaseBox: {
    marginTop: 8,
    padding: '8px 10px',
    backgroundColor: 'rgba(212, 175, 55, 0.04)',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 2,
  },
  barTrack: {
    height: 4,
    backgroundColor: 'rgba(192, 192, 192, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  encounterBox: {
    marginTop: 8,
    padding: '8px 10px',
    backgroundColor: 'rgba(220, 20, 60, 0.04)',
    border: '1px solid rgba(220, 20, 60, 0.2)',
    borderRadius: 2,
  },
  gmNotes: {
    marginTop: 8,
    padding: '8px 10px',
    backgroundColor: 'rgba(192, 192, 192, 0.03)',
    borderRadius: 2,
  },
  clueList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  clueCard: {
    padding: '8px 12px',
    border: '1px solid rgba(192, 192, 192, 0.1)',
    borderRadius: 2,
  },
  npcList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  npcCard: {
    padding: '10px 14px',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 2,
    backgroundColor: 'rgba(212, 175, 55, 0.03)',
  },
};
